import { useTranslation } from 'react-i18next';
import { Wrench } from 'lucide-react';
import { cn } from '@/lib/utils';

// The greyed "temporarily offline" button (owner 2026-10-16). It looks like
// the tool's button, cannot be pressed, and says why in a short line. It goes
// away by itself when the tool can run again (lib/tool-availability.ts).
type Tone = 'dark' | 'light';

export function OfflineButton({ className, compact = false, tone = 'dark' }: { className?: string; compact?: boolean; tone?: Tone }) {
  const { t } = useTranslation();
  return (
    <span
      role="note"
      aria-disabled="true"
      data-testid="tool-offline"
      className={cn(
        'flex w-full cursor-not-allowed select-none flex-col items-center justify-center rounded-full border px-5 text-center grayscale',
        tone === 'light' ? 'border-black/10 bg-black/[0.05] text-black/50' : 'border-white/10 bg-white/[0.06] text-white/55',
        compact ? 'min-h-10 py-1.5' : 'min-h-12 py-2',
        className,
      )}
    >
      <span className="flex items-center gap-2 text-[14.5px] font-bold">
        <Wrench className="h-4 w-4 shrink-0" aria-hidden />
        {t('offline.short')}
      </span>
      {!compact && <span className="mt-0.5 text-[12px] font-medium leading-snug">{t('offline.reason')}</span>}
    </span>
  );
}

/** A full panel where a tool's console would be. */
export function OfflinePanel({ className, tone = 'dark' }: { className?: string; tone?: Tone }) {
  const { t } = useTranslation();
  const light = tone === 'light';
  return (
    <div data-testid="tool-offline-panel" className={cn('mx-auto max-w-2xl rounded-3xl border p-6 text-center sm:p-8', light ? 'border-black/10 bg-white/70' : 'border-white/10 bg-white/[0.03]', className)}>
      <span className={cn('mx-auto flex h-12 w-12 items-center justify-center rounded-2xl', light ? 'bg-black/[0.05] text-black/50' : 'bg-white/[0.06] text-white/60')}>
        <Wrench className="h-6 w-6" aria-hidden />
      </span>
      <p className="mt-4 font-display text-[1.3rem] font-bold text-foreground">{t('offline.short')}</p>
      <p className="mt-2 text-[15px] text-muted-foreground">{t('offline.long')}</p>
      <OfflineButton tone={tone} className="mx-auto mt-5 max-w-sm" />
    </div>
  );
}
